import { HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { BehaviorSubject } from 'rxjs';

import { ApplicationHttpClient } from '../../application-http-client';
import { DownloadQueueItem, FileQueueStatus } from './files';

@Injectable()
export class FileDownloadService {
  private files: DownloadQueueItem[] = [];
  private currentQueue: BehaviorSubject<DownloadQueueItem[]>;

  public get queue() {
    return this.currentQueue.asObservable();
  }

  public downloadFile(apiEndpoint: string) {
    const item = new DownloadQueueItem(apiEndpoint);

    // set the individual object events
    item.download = () => this.downloadItem(item);
    item.remove = () => this.removeItem(item);
    item.cancel = () => this.cancelItem(item);

    this.addToQueue(item);
    return item;
  }

  public clearQueue() {
    this.files = [];
    this.currentQueue.next(this.files);
  }

  public downloadAll() {
    for (const item of this.files.filter(x => x.isDownloadable())) {
      this.downloadItem(item);
    }
  }

  public openFile(item: DownloadQueueItem) {
    if (!item.isDownloaded) {
      throw Error('The file has not completed downloading!');
    }
    window.open(URL.createObjectURL(item.file));
  }

  public getSafeURL(item: DownloadQueueItem) {
    if (!item.isDownloaded) {
      throw Error('The file has not completed downloading!');
    }
    return this.domSanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(item.file));
  }

  private addToQueue(item: DownloadQueueItem) {
    this.files.push(item);
    this.currentQueue.next(this.files);
  }

  private removeItem(item: DownloadQueueItem) {
    this.files = this.files.filter(x => x !== item);
    this.currentQueue.next(this.files);
  }

  private downloadItem(item: DownloadQueueItem) {
    item.request = this.http
      .get(item.apiEndpoint, { responseType: 'blob' as 'blob', observe: 'events', reportProgress: true, errorNotification: true })
      .subscribe(
        (event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              break;
            case HttpEventType.ResponseHeader:
              break;
            case HttpEventType.DownloadProgress:
              this.onProgressEvent(item, event);
              break;
            case HttpEventType.Response:
              this.onCompleteEvent(item, event);
              break;
          }
        },
        (error: HttpErrorResponse) => {
          if (error.error instanceof Error) {
            // A client-side or network error occurred. Handle it accordingly.
            this.onFailedEvent(item, error);
          } else {
            // The backend returned an unsuccessful response code.
            this.onFailedEvent(item, error);
          }
        }
      );

    return item;
  }

  private cancelItem(item: DownloadQueueItem) {
    item.request.unsubscribe();
    item.progress = 0;
    item.updateStatus(FileQueueStatus.Pending);
    this.currentQueue.next(this.files);
  }

  private onProgressEvent(item: DownloadQueueItem, event: any) {
    const progress = Math.round((100 * event.loaded) / event.total);
    item.progress = progress;
    item.updateStatus(FileQueueStatus.Progress);
    this.currentQueue.next(this.files);
 }

  private onCompleteEvent(item: DownloadQueueItem, response: HttpResponse<any>) {
    item.progress = 100;
    item.response = response;
    item.file = response.body;
    item.updateStatus(FileQueueStatus.Success);
    item.onComplete();
    this.currentQueue.next(this.files);
  }

  private onFailedEvent(item: DownloadQueueItem, response: HttpErrorResponse) {
    item.progress = 0;
    item.response = response;
    item.updateStatus(FileQueueStatus.Error);
    this.currentQueue.next(this.files);
  }

  constructor(private http: ApplicationHttpClient, private domSanitizer: DomSanitizer) {
    this.currentQueue = <BehaviorSubject<DownloadQueueItem[]>>new BehaviorSubject(this.files);
  }
}
